import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  IoBarcodeOutline,
  IoLayersOutline,
  IoCalendarOutline,
  IoStorefrontOutline,
  IoTimeOutline,
  IoDocumentTextOutline,
  IoPricetagOutline,
  IoCashOutline,
  IoPencil,
  IoSwapHorizontalOutline,
  IoTrashOutline,
  IoClose,
  IoBanOutline,
  IoWarningOutline,
} from "react-icons/io5";
import api, { fileUrl } from "@/api";
import { useI18n, ALL_STORES, STORE_LABELS } from "@/i18n";
import { deleteWithUndo } from "@/utils/undoToast";
import { useAuth } from "@/auth";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { formatDateIT } from "@/utils";
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription,
} from "@/components/ui/drawer";

const InfoRow = ({ Icon, label, value }) => (
  <div className="tf-info-row" data-testid={`info-${label}`}>
    <div className="tf-info-icon"><Icon size={20} /></div>
    <div className="tf-info-body">
      <div className="tf-info-label">{label}</div>
      <div className="tf-info-value">{value ?? "—"}</div>
    </div>
  </div>
);

export default function ProductDetailPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user, isTitolare, isDirettore, canAccessStore } = useAuth();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transferOpen, setTransferOpen] = useState(false);
  const [expireOpen, setExpireOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/products/${productId}`);
      setP(data);
    } catch (err) {
      console.error("Product load failed:", err);
      toast.error("Prodotto non trovato");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [productId, navigate]);

  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    // Optimistic navigate away; if user hits Annulla, restore product client-side by refetching.
    setConfirmOpen(false);
    let cancelled = false;
    const snapshot = p;
    navigate(-1);
    deleteWithUndo({
      label: `Articolo "${snapshot?.name || ""}" eliminato`,
      duration: 5000,
      onCommit: async () => {
        if (cancelled) return;
        try {
          await api.delete(`/products/${productId}`);
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Errore eliminazione");
        }
      },
      onUndo: () => {
        cancelled = true;
        toast.success("Ripristinato");
      },
    });
  };

  if (loading || !p) {
    return (
      <div className="mobile-frame">
        <div className="tf-header-simple">
          <button className="tf-close-btn" onClick={() => navigate(-1)}><IoClose size={22} /></button>
          <div className="tf-header-title-center">…</div>
          <div style={{ minWidth: 44 }} />
        </div>
        <div className="tf-empty">{t("loading")}</div>
      </div>
    );
  }

  const imgSrc = p.image_file_id ? fileUrl(p.image_file_id) : (p.image_url || "");
  const insertedDate = formatDateIT(p.created_at);
  const expiryDisplay = formatDateIT(p.expiry_date);

  // Detect if this product is already past its expiry date. When true the
  // "Trasferisci a un punto vendita" flow is disabled — the only allowed
  // destination is the Scaduti section (POST /products/{id}/move_to_expired).
  const isExpired = (() => {
    if (!p.expiry_date) return false;
    const [y, m, d] = String(p.expiry_date).split("-").map(Number);
    if (!y || !m || !d) return false;
    const target = new Date(y, m - 1, d);
    target.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return target.getTime() < today.getTime();
  })();

  return (
    <div className="mobile-frame" data-testid="product-detail-page">
      <div className="tf-header-simple">
        <button data-testid="pd-close" className="tf-close-btn" onClick={() => navigate(`/catalog/${p.store_id}`)} aria-label={t("cancel")}>
          <IoClose size={22} />
        </button>
        <div className="tf-header-title-center" data-testid="page-title">{p.name}</div>
        <div style={{ minWidth: 44 }} />
      </div>

      <div className="tf-content" style={{ paddingTop: 4 }}>
        <InfoRow Icon={IoBarcodeOutline} label={t("barcode")} value={p.barcode || "—"} />
        <InfoRow Icon={IoLayersOutline} label={t("quantity_available")} value={t("quantity_pieces").replace("$", p.quantity)} />
        <InfoRow Icon={IoCalendarOutline} label={t("expiry_date")} value={expiryDisplay} />
        <InfoRow Icon={IoStorefrontOutline} label={t("stores")} value={t(STORE_LABELS[p.store_id])} />
        <InfoRow Icon={IoTimeOutline} label={t("date_inserted")} value={insertedDate} />
        {p.cost != null && (
          <InfoRow
            Icon={IoCashOutline}
            label={t("cost")}
            value={`€ ${Number(p.cost).toFixed(2)}  ·  ${t("total")}: € ${(Number(p.cost) * Number(p.quantity || 0)).toFixed(2)}`}
          />
        )}
        {p.price != null && (
          <InfoRow
            Icon={IoPricetagOutline}
            label={t("price")}
            value={`€ ${Number(p.price).toFixed(2)}  ·  ${t("total")}: € ${(Number(p.price) * Number(p.quantity || 0)).toFixed(2)}`}
          />
        )}
        <InfoRow Icon={IoDocumentTextOutline} label={t("notes")} value={p.notes || "—"} />

        <div className="tf-stack" style={{ marginTop: 24 }}>
          {canAccessStore(p.store_id) ? (
            <>
              {isExpired && (
                <div
                  data-testid="pd-expired-banner"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "10px 12px",
                    borderRadius: 12,
                    background: "#0a0a0a",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: 14,
                  }}
                >
                  <IoBanOutline size={18} />
                  Prodotto scaduto — puoi solo spostarlo in Scaduti
                </div>
              )}
              <button data-testid="pd-edit" className="tf-btn-outline-red" onClick={() => navigate(`/product/${productId}/edit`)}>
                <IoPencil size={18} />
                {t("edit_article")}
              </button>
              {isExpired ? (
                <button
                  data-testid="pd-move-expired"
                  className="tf-btn-primary"
                  style={{ background: "#0a0a0a" }}
                  onClick={() => setExpireOpen(true)}
                >
                  <IoBanOutline size={18} />
                  Sposta in Scaduti
                </button>
              ) : (
                <button data-testid="pd-transfer" className="tf-btn-primary" onClick={() => setTransferOpen(true)}>
                  <IoSwapHorizontalOutline size={18} />
                  {t("transfer_to")}
                </button>
              )}
              <button data-testid="pd-delete" className="tf-btn-outline-red" onClick={() => setConfirmOpen(true)}>
                <IoTrashOutline size={18} />
                {t("delete_article")}
              </button>
            </>
          ) : (
            <>
              <div className="tf-view-only-banner" data-testid="view-only-banner">
                {t(STORE_LABELS[p.store_id])}
              </div>
            </>
          )}
        </div>
      </div>

      {!isExpired && (
        <TransferSheet open={transferOpen} onOpenChange={setTransferOpen} product={p} onDone={load} />
      )}
      <MoveToExpiredSheet
        open={expireOpen}
        onOpenChange={setExpireOpen}
        product={p}
        onDone={() => { load(); navigate(`/catalog/${p.store_id}`); }}
      />
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={remove}
        title={t("confirm_delete")}
        description={p.name}
        confirmLabel={t("delete")}
      />
    </div>
  );
}

const TransferSheet = ({ open, onOpenChange, product, onDone }) => {
  const { t } = useI18n();
  const [dest, setDest] = useState("");
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!dest) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/products/${product.id}/transfer`, {
        destination_store_id: dest,
        quantity: Number(qty),
      });
      toast.success(data.merged ? t("merged_note") : t("transfer_done"));
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore trasferimento");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent data-testid="transfer-drawer">
        <DrawerHeader style={{ textAlign: "left" }}>
          <DrawerTitle>{t("transfer_title")}</DrawerTitle>
          <DrawerDescription>{product?.name}</DrawerDescription>
        </DrawerHeader>
        <div style={{ padding: "0 20px 24px" }} data-vaul-no-drag onPointerDownCapture={(e) => e.stopPropagation()}>
          <div className="tf-field">
            <label className="tf-label">{t("destination")}</label>
            <select
              className="tf-select"
              value={dest}
              onChange={(e) => setDest(e.target.value)}
              data-testid="transfer-destination"
              data-vaul-no-drag
              onPointerDownCapture={(e) => e.stopPropagation()}
            >
              <option value="">—</option>
              {ALL_STORES.filter((s) => s !== product.store_id).map((s) => (
                <option key={s} value={s}>{t(STORE_LABELS[s])}</option>
              ))}
            </select>
          </div>
          <div className="tf-field">
            <label className="tf-label">{t("quantity_to_transfer")} (max {product.quantity})</label>
            <input
              data-testid="transfer-quantity"
              className="tf-input"
              type="number"
              min={1}
              max={product.quantity}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              inputMode="numeric"
              data-vaul-no-drag
              onPointerDownCapture={(e) => e.stopPropagation()}
            />
          </div>
          <button
            data-testid="transfer-submit"
            className="tf-btn-primary"
            onClick={submit}
            disabled={busy || !dest}
            data-vaul-no-drag
          >
            {busy ? t("loading") : t("confirm")}
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
};


const MoveToExpiredSheet = ({ open, onOpenChange, product, onDone }) => {
  const [dest, setDest] = useState("");
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  // Reset fields whenever the sheet opens (or product changes) so left-over
  // input from previous sessions doesn't leak between products.
  useEffect(() => {
    if (open && product) {
      setDest("");
      setQty(product.quantity || 1);
      setNotes("");
    }
  }, [open, product]);

  const submit = async () => {
    if (!dest.trim()) { toast.error("Inserisci la destinazione"); return; }
    const n = Number(qty);
    if (!Number.isFinite(n) || n < 1 || n > (product?.quantity || 0)) {
      toast.error("Quantità non valida");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/products/${product.id}/move_to_expired`, null, {
        params: {
          expiry_date: product.expiry_date || "",
          quantity: n,
          destination: dest.trim(),
          notes: notes.trim(),
        },
      });
      toast.success("Spostato in Scaduti");
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante lo spostamento");
    } finally {
      setBusy(false);
    }
  };

  if (!product) return null;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent data-testid="move-expired-drawer">
        <DrawerHeader style={{ textAlign: "left" }}>
          <DrawerTitle style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <IoBanOutline size={20} />
            Sposta in Scaduti
          </DrawerTitle>
          <DrawerDescription>{product?.name}</DrawerDescription>
        </DrawerHeader>
        <div style={{ padding: "0 20px 24px" }} data-vaul-no-drag onPointerDownCapture={(e) => e.stopPropagation()}>
          <div className="tf-field">
            <label className="tf-label">Destinazione *</label>
            <input
              data-testid="move-expired-destination"
              className="tf-input"
              type="text"
              value={dest}
              onChange={(e) => setDest(e.target.value)}
              placeholder="Es. reso fornitore, distruzione, beneficenza…"
              data-vaul-no-drag
              onPointerDownCapture={(e) => e.stopPropagation()}
            />
          </div>
          <div className="tf-field">
            <label className="tf-label">Quantità (max {product.quantity})</label>
            <input
              data-testid="move-expired-quantity"
              className="tf-input"
              type="number"
              min={1}
              max={product.quantity}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              onFocus={(e) => e.target.select()}
              inputMode="numeric"
              data-vaul-no-drag
              onPointerDownCapture={(e) => e.stopPropagation()}
            />
          </div>
          <div className="tf-field">
            <label className="tf-label">Note (opzionale)</label>
            <input
              data-testid="move-expired-notes"
              className="tf-input"
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Motivo, riferimenti…"
              data-vaul-no-drag
              onPointerDownCapture={(e) => e.stopPropagation()}
            />
          </div>
          <button
            data-testid="move-expired-submit"
            className="tf-btn-primary"
            style={{ background: "#0a0a0a" }}
            onClick={submit}
            disabled={busy || !dest.trim()}
            data-vaul-no-drag
          >
            <IoBanOutline size={18} />
            {busy ? "Sto spostando…" : "Conferma spostamento"}
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
